// validations.js
export const validateSingleField = (name, value, formData) => {
  const errors = {};

  const normalizeWhatsapp = (val) => {
    if (!val) return '';
    let s = String(val).trim();
    // remove outer parentheses repeatedly: "(...)" -> ...
    while (s.startsWith('(') && s.endsWith(')')) {
      s = s.slice(1, -1).trim();
    }
    // remove common invisible chars that might break regex
    s = s.replace(/\u00A0/g, ' ').replace(/\uFEFF/g, '').trim();
    return s;
  };

  if (name === 'gender' && !value) {
    errors[name] = 'Gender is required';
  }
  if (name === 'category' && !value) {
    errors[name] = 'Category is required';
  }
  if (name === 'agency' && !value) {
    errors[name] = 'Agency is required';
  }
  if (name === 'customCategory' && formData.category === 'Other' && !value) {
    errors[name] = 'Custom category is required when "Other" is selected';
  }
  if (name === 'customSubcategory' && formData.subcategory === 'Other' && !value) {
    errors[name] = 'Custom subcategory is required when "Other" is selected';
  }
  if (name === 'customLanguage' && formData.language === 'Other' && !value) {
    errors[name] = 'Custom language is required when "Other" is selected';
  }
  if (name === 'hline1' && !value) {
    errors[name] = 'Heading Line 1 is required';
  }
  if (name === 'content' && !value) {
    errors[name] = 'Description is required';
  }
  if (name === 'contactbg' && !value) {
    errors[name] = 'Contact theme is required';
  }
  if (name === 'hlinebg' && !value) {
    errors[name] = 'Heading theme is required';
  }
  if (name === 'contentfs' && !value) {
    errors[name] = 'Description font size is required';
  }
  if (name === 'InsertDate' && !value) {
    errors[name] = 'Insert Date is required';
  }


  // WhatsApp validation if provided (single-field/inline)
  if (name === 'whatsapp' && value) {
    const cleaned = normalizeWhatsapp(value);
    // Allow variations: "WA", "W/A", "W A", optional "+", 8-15 digits, optional " (name)" after number
    const whatsappPattern = /^.*?(?:\s*)?\+?\d{8,15}(?:\s*\([^)]+\))?/i;
    if (!whatsappPattern.test(cleaned)) {
      errors[name] = 'WhatsApp format: optional "WA" or "W/A", optional "+", 8–15 digits, optional "(name)"';
    }
  }

  // Validate email if provided
  if (name === 'email' && value) {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const disposableDomains = [
      'mailinator.com', 'tempmail.com', 'guerrillamail.com', '10minutemail.com',
      'throwawaymail.com', 'yopmail.com', 'sharklasers.com', 'trashmail.com',
      'getnada.com', 'temp-mail.org', '10minutemail.net', '10minutemail.de',
      '10minutemail.ga', '10minutemail.gq', '10minutemail.ml', '10minutemail.co.za',
      '10minutemail.com.pl', 'temp-mail.io', 'tempail.com', 'mail.tm', 'tempmailo.com',
      'fakemail.net', 'ikanteri.com', 'tmmwj.com', 'ezynow.com', 'mohmal.com',
      'maildrop.cc', 'disposablemail.com', 'tempemail.net', 'tempmail.net',
      'throwawayemail.com', 'fakeinbox.com'
    ];
    const domain = value.split('@')[1]?.toLowerCase();

    if (!emailPattern.test(value)) {
      errors[name] = 'Please enter a valid email address (e.g., user@domain.com)';
    } else if (disposableDomains.includes(domain)) {
      errors[name] = 'Disposable or temporary email addresses are not allowed';
    }
  }

  return errors;
};

export const validateForm = (formData) => {
  const errors = {};

  const normalizeWhatsapp = (val) => {
    if (!val) return '';
    let s = String(val).trim();
    while (s.startsWith('(') && s.endsWith(')')) {
      s = s.slice(1, -1).trim();
    }
    s = s.replace(/\u00A0/g, ' ').replace(/\uFEFF/g, '').trim();
    return s;
  };

  // Base required fields (always)
  const requiredFields = ['gender', 'agency', 'category', 'content', 'contentbg', 'contactbg', 'InsertDate'];

  // Add headline-specific requirements only if Size is selected
  if (formData.space === "Box") {
    requiredFields.push('hline1', 'hlinebg');
  }

  requiredFields.forEach((field) => {
    if (!formData[field]) {
      errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }
  });

  // Validate custom fields when "Other" is selected
  if (formData.category === 'Other' && !formData.customCategory) {
    errors.customCategory = 'Custom category is required';
  }
  if (formData.subcategory === 'Other' && !formData.customSubcategory) {
    errors.customSubcategory = 'Custom subcategory is required';
  }
  if (formData.language === 'Other' && !formData.customLanguage) {
    errors.customLanguage = 'Custom language is required';
  }

  // Heading validation only if Size
  if (formData.space === "Box" && !formData.hline1) {
    errors.hline1 = 'Heading Line 1 is required';
  }

  // At least one contact
  const hasWhatsapp = Boolean(String(formData.whatsapp || '').trim());
  const hasMobile = Boolean(String(formData.mobile || '').trim());
  const hasEmail = Boolean(String(formData.email || '').trim());

  if (!hasWhatsapp && !hasMobile && !hasEmail) {
    errors.contact = 'At least one of WhatsApp, Contact No., or Email is required';
  }

  // WhatsApp validation
  if (hasWhatsapp) {
    const cleaned = normalizeWhatsapp(formData.whatsapp);
    const whatsappPattern = /^.*?(?:\s*)?\+?\d{8,15}(?:\s*\([^)]+\))?/i;
    if (!whatsappPattern.test(cleaned)) {
      errors.whatsapp = 'WhatsApp format: optional "WA" or "W/A", optional "+", 8-15 digits, optional "(name)"';
    }
  }

  // Email validation
  if (hasEmail) {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const disposableDomains = [
      'mailinator.com','tempmail.com','guerrillamail.com','10minutemail.com',
      'throwawaymail.com','yopmail.com','sharklasers.com','trashmail.com',
      'getnada.com','temp-mail.org','10minutemail.net','10minutemail.de',
      '10minutemail.ga','10minutemail.gq','10minutemail.ml','10minutemail.co.za',
      '10minutemail.com.pl','temp-mail.io','tempail.com','mail.tm','tempmailo.com',
      'fakemail.net','ikanteri.com','tmmwj.com','ezynow.com','mohmal.com',
      'maildrop.cc','disposablemail.com','tempemail.net','tempmail.net',
      'throwawayemail.com','fakeinbox.com'
    ];
    const domain = formData.email.split('@')[1]?.toLowerCase();
    if (!emailPattern.test(formData.email)) {
      errors.email = 'Please enter a valid email address (e.g., user@domain.com)';
    } else if (disposableDomains.includes(domain)) {
      errors.email = 'Disposable or temporary email addresses are not allowed';
    }
  }

  return errors;
};

